import { Operator } from '@procureai/query-builder';
import {
  ValueEditorDate,
  ValueEditorDateRange,
  ValueEditorInput,
  ValueEditorSelect,
} from '@procureai/query-builder-ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SvbrgDocumentListFilterField, SvbrgDocumentVariant } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import {
  UseDataGridQueryBuilderConfig,
  listOperators,
  operators,
  queryFieldFactory,
} from '~/components/DataGridQueryBuilder';
import { ValueEditorSelectAutocomplete } from '~/components/ValueEditorSelectAutocomplete/ValueEditorSelectAutocomplete';

enum ProcurementKnowledgeListQueryFieldGroup {
  DOCUMENT = 'document',
}

export const useProcurementKnowledgeListDataGridQueryBuilderConfig: UseDataGridQueryBuilderConfig = () => {
  const { t } = useTranslation(I18nNamespace.knowledgeRepository);
  return useMemo(() => {
    const createField = queryFieldFactory(t, { i18nPrefix: 'ProcurementKnowledgeListQueryBuilder' });
    return {
      fields: [
        createField({
          name: SvbrgDocumentListFilterField.title,
          grp: ProcurementKnowledgeListQueryFieldGroup.DOCUMENT,
          operators: operators.text,
        }),
        createField({
          name: SvbrgDocumentListFilterField.fileName,
          grp: ProcurementKnowledgeListQueryFieldGroup.DOCUMENT,
          operators: operators.text,
        }),
        createField({
          name: SvbrgDocumentListFilterField.variant,
          grp: ProcurementKnowledgeListQueryFieldGroup.DOCUMENT,
          operators: operators.enumValueNotEmpty,
        }),
        createField({
          name: SvbrgDocumentListFilterField.updatedAt,
          grp: ProcurementKnowledgeListQueryFieldGroup.DOCUMENT,
          operators: operators.date,
        }),
      ],
      getOperatorLabel: ({ field, operator }) => {
        switch (field) {
          case SvbrgDocumentListFilterField.updatedAt:
            if (operator === Operator.LT || operator === Operator.GT) {
              return `QueryBuilder.operatorDate.${operator}`;
            }
        }
      },
      renderValueEditor: ({ field, operator, ...otherProps }) => {
        if (operator === Operator.EMPTY || operator === Operator.NOT_EMPTY) {
          return null;
        }
        switch (field) {
          //
          case SvbrgDocumentListFilterField.variant:
            return (
              <ValueEditorSelectAutocomplete
                {...otherProps}
                ns={I18nNamespace.knowledgeRepository}
                initialI18nPrefix="ProcurementKnowledgeVariant"
                initialOptions={Object.keys(SvbrgDocumentVariant)}
                multiple={listOperators[operator]}
              />
            );
          case SvbrgDocumentListFilterField.updatedAt:
            if (operator === Operator.BETWEEN) {
              return <ValueEditorDateRange {...otherProps} />;
            }
            return <ValueEditorDate {...otherProps} />;
          case SvbrgDocumentListFilterField.title:
          case SvbrgDocumentListFilterField.fileName:
            if (listOperators[operator]) {
              return <ValueEditorSelect {...otherProps} multiple={true} freeSolo />;
            }
            return <ValueEditorInput {...otherProps} />;
          //
          default:
            return null;
        }
      },
    };
  }, [t]);
};
